import { useContext, FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { css } from '@appd/uik';
import { TimeRangeSelector } from '@appd/uik/@future';
import { ShellEvent, useShellTimeRange } from '@appd/fso-ui-shell/container';
import { useQueryClient } from '@tanstack/react-query';
import { Handler } from 'mitt';
import noop from 'lodash/noop';
import isFunction from 'lodash/isFunction';
import pickBy from 'lodash/pickBy';
import { DashboardContext, useDashboard, useShellEvents, useUserInfo } from '../../../hooks';
import { DashboardSourceCodeMemoized } from '.';
import { DashboardRenderer } from './DashboardRenderer';
import { DEFAULT_TIME_RANGE_SINCE, DEFAULT_TIME_RANGE_UNTIL } from '../../../constants';
import { Dashboard, DashboardJson } from '../../models';
import { setGlobals, initEmitter, initGlobals } from './globals';
import {
  getEffectiveTimeRangeFromGlobalsVariables,
  variablesFromURLParams,
  updateURLWithGlobals,
} from '../../../utils';
import useEmitter from './hooks/useEmitter';
import useHandleRouteChange from './hooks/useHandleRouteChange';
import useDashboardData from './hooks/useDashboardData';
import DashboardViewerHeader from './DashboardViewerHeader';
import navigateTo from './utils/navigateTo';
import arePreInitConditionsSatisfied from './utils/arePreInitConditionsSatisfied';
import isValidUserInfo from './utils/isValidUserInfo';
import isValidCurrentTheme from './utils/isValidCurrentTheme';
import { DashboardLoading } from '../../components/componentVariations/loading';
import { logger } from '../../../utils';
import { GLOBALS_VARIABLES_SINCE, GLOBALS_VARIABLES_UNTIL, GLOBALS_THEME, GLOBALS_USERINFO } from '../../../constants';

interface DashboardViewerProps {
  dashboardName: string;
  filter: string | undefined;
  isEditorMode: boolean;
  useDashifyEditor: boolean;
  useAdvancedDashifyEditor?: boolean;
  showPageTitle?: boolean;
  fetchFromOrion?: boolean;
  onTemplateFetched?: (res: { data: DashboardJson }) => void;
}

/** ***********************************************************************************************************
 *  DashboardViewer
 ** ***********************************************************************************************************/

export const DashboardViewer: FC<DashboardViewerProps> = ({
  dashboardName,
  filter,
  isEditorMode,
  useDashifyEditor,
  useAdvancedDashifyEditor,
  showPageTitle,
  fetchFromOrion = true,
  onTemplateFetched = noop,
}): JSX.Element | null => {
  const router = useRouter();
  const routerObj = pickBy(router, (value: any) => !isFunction(value));
  const queryClient = useQueryClient();
  const emitter = useEmitter();
  const { isOpenSourceCode } = useContext(DashboardContext);
  const { currentTheme } = useContext(css.CONTEXT);
  const userInfo = useUserInfo(`${process.env.NEXT_PUBLIC_IDENTITY_API_URL}`);
  const { timeRange } = useShellEvents({
    since: DEFAULT_TIME_RANGE_SINCE,
    until: '',
  });
  const [isDashboardReady, setIsDashboardReady] = useState(false);

  // we want to empty all URL params except since, until when we unmount the component
  useHandleRouteChange(router);

  const { dashboardData, isTenantDashboard } = useDashboardData({ dashboardName, fetchFromOrion });
  const dashboard = useDashboard({ name: dashboardName }) as Dashboard;
  // without this the emits to 'stateTimeRangeUpdated' is not acknowledged
  const { getUpdatedTimeRange, shellTimeRange } = useShellTimeRange();

  // ------------------------------------- getDashboard() ---------------------------------------------------------

  useEffect(() => {
    if (dashboardData != null) {
      onTemplateFetched(dashboardData);
    }
  }, [dashboardData, onTemplateFetched]);

  // ------------------------------------- useEffect on [emitter] ----------------------------------------------------

  useEffect(() => {
    if (!emitter) {
      return;
    }
    initEmitter(emitter);
    emitter.on('navigateTo', navigateTo as Handler<unknown>);
    emitter.on('globalsChanged', updateURLWithGlobals);

    return () => {
      emitter && emitter.off('navigateTo', navigateTo as Handler<unknown>);
      emitter && emitter.off('globalsChanged', updateURLWithGlobals);
    };
  }, [emitter]);

  // ------------------------------------- useEffect on [queryClient, ShellEvent] -------------------------------------

  useEffect(() => {
    if (!useAdvancedDashifyEditor) {
      const handleRefresh = (trEvent: TimeRangeSelector.TimeRange.Event) => {
        if (trEvent.eventSource === 'REFRESH' || trEvent.eventSource === 'AUTO_REFRESH') {
          queryClient.invalidateQueries();
        }

        // globals.variables.[since|until] will maintain absolute timerange
        setGlobals(GLOBALS_VARIABLES_SINCE, trEvent.value.input.since);
        if (trEvent.value.input.until) {
          setGlobals(GLOBALS_VARIABLES_UNTIL, trEvent.value.input.until);
        } else {
          setGlobals(GLOBALS_VARIABLES_UNTIL, DEFAULT_TIME_RANGE_UNTIL);
        }
      };

      ShellEvent.on('timeRangeChanged', handleRefresh);

      return () => {
        ShellEvent.un('timeRangeChanged', handleRefresh);
      };
    }
  }, [queryClient, ShellEvent]);

  // ------------------------------------- useEffect on [routerObj] ---------------------------------------------------------

  // Removing this will break the URL query for csaapp on navigation via left-nav.
  // TODO: Remove unused "routerObj"
  useEffect(() => {
    setGlobals('/globals/router', routerObj);
  }, [routerObj]);

  // ------------------------------------- useEffect on [currentTheme] ---------------------------------------------------------

  useEffect(() => {
    if (isDashboardReady && isValidCurrentTheme(currentTheme)) {
      setGlobals(GLOBALS_THEME, currentTheme);
    }
  }, [currentTheme]);

  // ------------------------------------- useEffect on [userInfo] -------------------------------------------------------------

  useEffect(() => {
    if (isDashboardReady) {
      setGlobals(GLOBALS_USERINFO, userInfo);
    }
  }, [userInfo]);

  // ------------------------------------- useEffect on [] ----------------------------------------------------------------

  useEffect(() => {
    if (!arePreInitConditionsSatisfied(router, timeRange, currentTheme, userInfo, isDashboardReady)) {
      logger.log('skipped global initialization due to unmet pre-initialization conditions');
      return;
    }

    const variablesFromURL = variablesFromURLParams(router);
    const effectiveTimeRange = getEffectiveTimeRangeFromGlobalsVariables(variablesFromURL);

    const initializeGlobalsCallback = (isInitialized: boolean) => {
      if (isInitialized) {
        ShellEvent.emit('stateTimeRangeUpdated', effectiveTimeRange);
      }
      setIsDashboardReady(isInitialized);
    };

    const initializeGlobals = () => {
      initGlobals(
        {
          variables: {
            ...variablesFromURL,
            since: effectiveTimeRange.input.since,
            until: effectiveTimeRange.input.until,
          },
          baseUrl: window.location.origin,
          filter,
          currentTheme,
          userInfo,
        },
        initializeGlobalsCallback,
      );
    };

    initializeGlobals();
  }, []);

  // ---------------------------------------- render dashboard -------------------------------------------------------------

  if (!isDashboardReady) {
    return <DashboardLoading />;
  }

  return (
    <div className="flex flex-col h-full min-w-0 bg-inherit">
      <DashboardViewerHeader
        showPageTitle={Boolean(showPageTitle)}
        dashboardJson={dashboard.json}
        isTenantDashboard={isTenantDashboard}
        dashboardName={dashboardName}
        isEditorMode={isEditorMode}
        useDashifyEditor={useDashifyEditor}
        useAdvancedDashifyEditor={Boolean(useAdvancedDashifyEditor)}
      />
      <DashboardRenderer name={dashboardName} />
      <DashboardSourceCodeMemoized isOpen={isOpenSourceCode} dashboard={dashboard.json} />
    </div>
  );
};
